import React, { useState, useEffect, useRef } from "react";
import Icon from "./Icon";
import Modal from "./Modal";
import { getPartnerSignInUrl } from "../utils/appLinks";

/**
 * Where each download option sends the visitor. Kept in one place so the
 * links can be updated without touching the dialog.
 */
export const APP_LINKS = {
  user: "/download-user",
  business: "/download-business",
  dashboard: getPartnerSignInUrl()
};

/** Shows the visitor where a link actually goes before they commit to it. */
function hostOf(url) {
  try {
    return new URL(url, window.location.origin).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

/* ------------------------------------------------------------------ */
/*  Option row                                                          */
/* ------------------------------------------------------------------ */

const OPTION_CLASS =
  "group flex w-full items-center gap-5 rounded-2xl border border-[#EAE2D6] bg-white p-5 text-left transition-all duration-300 hover:-translate-y-0.5 hover:border-[#D8B4FE] hover:shadow-[0_22px_45px_-26px_rgba(106,13,173,0.5)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#6A0DAD]/20";

function OptionBody({ icon, title, description, meta, trailing }) {
  return (
    <>
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#F4E8FF] to-[#EADCFB] text-[#6A0DAD] ring-1 ring-[#EBDBFA] transition-all duration-300 group-hover:from-[#6A0DAD] group-hover:to-[#4C087D] group-hover:text-white group-hover:ring-[#6A0DAD]">
        <Icon name={icon} className="h-6 w-6" />
      </span>

      <span className="min-w-0 flex-1">
        <span className="block text-[17px] font-semibold tracking-tight text-[#1E1B16]">
          {title}
        </span>
        <span className="mt-1 block text-[14px] leading-relaxed text-[#4D4B46]">
          {description}
        </span>
        {meta && (
          <span className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-[#7A6F61]">
            <Icon name="external" className="h-3.5 w-3.5" />
            {meta}
          </span>
        )}
      </span>

      <Icon
        name={trailing}
        className="h-5 w-5 shrink-0 text-[#9A9085] transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#6A0DAD]"
      />
    </>
  );
}

function OptionLink({ href, onNavigate, ...rest }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      onClick={onNavigate}
      className={OPTION_CLASS}
    >
      <OptionBody {...rest} meta={hostOf(href)} trailing="arrow" />
    </a>
  );
}

function OptionButton({ onClick, ...rest }) {
  return (
    <button type="button" onClick={onClick} className={OPTION_CLASS}>
      <OptionBody {...rest} trailing="arrow" />
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Dialog                                                              */
/* ------------------------------------------------------------------ */

function DownloadDialog({ open, onClose }) {
  const [step, setStep] = useState("root");
  const stepRef = useRef(null);
  const prevStep = useRef("root");

  // Always reopen on the first question rather than wherever it was left.
  useEffect(() => {
    if (open) setStep("root");
  }, [open]);

  // Changing step unmounts the button that was focused, which would drop
  // focus onto <body> and let Tab escape the dialog. Move it into the new
  // step instead.
  useEffect(() => {
    if (!open) {
      prevStep.current = "root";
      return;
    }
    if (prevStep.current !== step) {
      stepRef.current?.querySelector("a, button")?.focus();
      prevStep.current = step;
    }
  }, [step, open]);

  const onBusiness = step === "business";

  return (
    <Modal open={open} onClose={onClose} labelledBy="download-dialog-title">
      <div className="p-8 sm:p-10">
        {onBusiness ? (
          <div key="business" ref={stepRef} className="mw-step">
            <button
              type="button"
              onClick={() => setStep("root")}
              className="group -ml-2 inline-flex items-center gap-2 rounded-lg px-2 py-1.5 text-[14px] font-semibold text-[#6A0DAD] transition-colors duration-200 hover:text-[#4C087D] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#6A0DAD]/20"
            >
              <Icon
                name="arrowLeft"
                className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1"
              />
              Back
            </button>

            <span className="mt-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#6A0DAD] to-[#4C087D] text-white shadow-xl shadow-[#6A0DAD]/30">
              <Icon name="store" className="h-7 w-7" />
            </span>

            <h2
              id="download-dialog-title"
              className="mt-6 text-2xl font-bold tracking-tight text-[#1E1B16] sm:text-[28px]"
            >
              Mondoway Business
            </h2>
            <p className="mt-3 text-[15px] leading-relaxed text-[#4D4B46]">
              Run your business from your phone, or manage everything from the partner
              dashboard in your browser.
            </p>

            <div className="mt-8 space-y-3">
              <OptionLink
                href={APP_LINKS.business}
                onNavigate={onClose}
                icon="smartphone"
                title="Business app"
                description="Manage listings, orders and customers on the go."
              />
              <OptionLink
                href={APP_LINKS.dashboard}
                onNavigate={onClose}
                icon="grid"
                title="Partner dashboard"
                description="The full web dashboard for products, orders and reporting."
              />
            </div>
          </div>
        ) : (
          <div key="root" ref={stepRef} className="mw-step">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#6A0DAD] to-[#4C087D] text-white shadow-xl shadow-[#6A0DAD]/30">
              <Icon name="smartphone" className="h-7 w-7" />
            </span>

            <h2
              id="download-dialog-title"
              className="mt-6 text-2xl font-bold tracking-tight text-[#1E1B16] sm:text-[28px]"
            >
              Which app do you need?
            </h2>
            <p className="mt-3 text-[15px] leading-relaxed text-[#4D4B46]">
              Choose the version of Mondoway that fits you — one for shopping and
              booking, one for running a business.
            </p>

            <div className="mt-8 space-y-3">
              <OptionLink
                href={APP_LINKS.user}
                onNavigate={onClose}
                icon="smartphone"
                title="User app"
                description="Shop, book and order across all twelve Mondoway services."
              />
              <OptionButton
                onClick={() => setStep("business")}
                icon="store"
                title="Business app"
                description="For merchants and partners — two options available."
              />
            </div>
          </div>
        )}

        <p className="mt-7 border-t border-[#F0E9DE] pt-5 text-[13px] leading-relaxed text-[#7A6F61]">
          Links open in a new tab.
        </p>
      </div>
    </Modal>
  );
}

export default DownloadDialog;
